import { create } from 'zustand';

import { settings } from '@/lib/db';
import { type LicensePayload, updateCovered } from '@/lib/license';
import { isTauri } from '@/lib/platform';

/**
 * Desktop app updates — `docs/DESKTOP.md`.
 *
 * Tauri's updater fetches a signed manifest of the latest release and, if it
 * is newer, downloads and verifies the update against the public key built
 * into the app before installing anything. Checked once a day at most,
 * silently; the result is offered in `UpdateBanner`, never installed
 * unasked. A release newer than the licence's update period is reported but
 * not offered for install (`updateCovered`).
 */

export interface DesktopUpdate {
  version: string;
  /** Publication date from the release manifest, when it gives one. */
  date?: string | undefined;
  covered: boolean;
  install: () => Promise<void>;
}

interface DesktopUpdateState {
  available: DesktopUpdate | null;
  /** The outcome of a check the pilot asked for, so it can be said. */
  lastManualResult: 'none' | 'error' | null;
}

export const useDesktopUpdate = create<DesktopUpdateState>(() => ({
  available: null,
  lastManualResult: null,
}));

const LAST_CHECK_KEY = 'device.lastUpdateCheck';
const CHECK_INTERVAL_MS = 24 * 60 * 60 * 1000;

export async function checkForDesktopUpdate({
  manual = false,
  license = null,
}: { manual?: boolean; license?: LicensePayload | null } = {}): Promise<void> {
  if (!isTauri()) {
    return;
  }
  if (!manual) {
    const last = await settings.get<string | null>(LAST_CHECK_KEY, null);
    if (last !== null && Date.now() - Date.parse(last) < CHECK_INTERVAL_MS) {
      return;
    }
  }
  try {
    const { check } = await import('@tauri-apps/plugin-updater');
    const update = await check();
    await settings.set(LAST_CHECK_KEY, new Date().toISOString());
    if (update === null) {
      useDesktopUpdate.setState({ available: null, lastManualResult: manual ? 'none' : null });
      return;
    }
    const covered = update.date === undefined ? true : updateCovered(license, update.date);
    useDesktopUpdate.setState({
      lastManualResult: null,
      available: {
        version: update.version,
        date: update.date,
        covered,
        install: async () => {
          await update.downloadAndInstall();
          const { relaunch } = await import('@tauri-apps/plugin-process');
          await relaunch();
        },
      },
    });
  } catch {
    // Offline, or a build with no update key configured. An unattended
    // check says nothing; one the pilot asked for says it failed.
    useDesktopUpdate.setState({ lastManualResult: manual ? 'error' : null });
  }
}
